
import * as React from 'react';
import * as PropTypes from 'prop-types';
import { IReactChildrenProps } from './Props';

/**
 * @public
 */
export interface IFragmentProps {
    /**
     * Whether or not the fragment should render any layout, while still being mounted.
     */
    hidden?: boolean;
}

/**
 * The default implementation of Fragment, which renders a <div />
 * On React 16, this should render a React.Fragment.
 * @internal
 */
export const DefaultFragment: React.ComponentType<IFragmentProps & IReactChildrenProps> = React.Fragment;

/**
 * @internal
 */
export interface IFragmentContext {
    fragment?: {
        override: React.ComponentType<IFragmentProps>;
    };
}

const FRAGMENT_CONTEXT_TYPES: React.ValidationMap<IFragmentContext> = {
    fragment: PropTypes.any
};

/**
 * @public
 */
export interface IFragmentProviderProps {
    // The React component type used to render fragments around child components.
    override: React.ComponentType<IFragmentProps>;
}

/**
 * Renders a fragment, which is intended to be an element-free wrapper for other components,
 * while being a valid React child element.
 *
 * Dependending on context, the chosen implementation of a Fragment may differ by platform.
 * @public
 */
export const Fragment: React.ComponentType<IFragmentProps> = React.Fragment;

/**
 * Provides configuration for a hierarchy of components which can be used to override the
 * component used to render 'fragments', or element-free wrappers for child controls.
 *
 * Use this to replace the default implementation for a Fragment, which currently renders
 * a <div /> and which will not work on native.
 * @public
 */
export class FragmentProvider extends React.PureComponent<IFragmentProviderProps> {
    public static childContextTypes: React.ValidationMap<IFragmentContext> = FRAGMENT_CONTEXT_TYPES;

    public getChildContext(): IFragmentContext {
        return {
            fragment: {
                override: this.props.override
            }
        };
    }

    public render(): JSX.Element {
        return (
            <Fragment>{ this.props.children }</Fragment>
        );
    }
}
